import { lazy, type ComponentType } from 'react';
import { isChunkLoadError, isReloading, recoverFromChunkError, RELOAD_GRACE_MS } from './chunk-recovery';

/**
 * `React.lazy` 대체 — 배포 후 옛 청크 404(동적 import 거부)를 사용자가 오류 카드로 보기 전에 흡수한다.
 *
 * - import가 청크 로드 오류로 거부되면 `recoverFromChunkError()`로 한 번의 조용한 리로드를 시작하고,
 *   리로드가 진행되는 동안 Suspense 폴백(로더)을 유지한다. 단 리로드가 무산될 수 있으므로(beforeunload 취소,
 *   bfcache 복원) 영원히 기다리지 않고 RELOAD_GRACE_MS 뒤 원래 오류로 reject해 바운더리의 청크 안내 카드가 뜨게 한다.
 * - 복구 불가(60초 쿨다운)이거나 청크 오류가 아닌 경우는 그대로 던져 에러 바운더리가 처리한다.
 * - Vite 7 프리로드 헬퍼는 `vite:preloadError` 리스너가 `preventDefault()`하면 import를 에러 대신
 *   `undefined`로 resolve한다. 그대로 두면 React.lazy가 `undefined.default`로 일반 오류를 던져 오류 카드가
 *   잠깐 보이므로, "리로드 중인데 모듈이 null/undefined"인 경우도 대기로 처리한다.
 *
 * 설계: docs/update-chunk-recovery-plan.md
 */

/**
 * 리로드가 진행되는 동안 Suspense 폴백(로더)을 유지하는 Promise.
 * RELOAD_GRACE_MS 안에 페이지가 교체되지 않으면(리로드 무산) 원래 오류로 reject해 바운더리로 전파한다.
 * 원래 오류가 없으면(undefined 모듈 경로) isChunkLoadError가 참인 문구의 오류를 쓴다.
 */
function waitForReload(err?: unknown): Promise<never> {
  return new Promise<never>((_resolve, reject) => {
    setTimeout(() => {
      reject(err ?? new TypeError('Failed to fetch dynamically imported module: reload did not happen'));
    }, RELOAD_GRACE_MS);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRecovery<T extends ComponentType<any>>(importer: () => Promise<{ default: T }>) {
  return lazy(() =>
    importer().then(
      (mod) => {
        // vite:preloadError 리스너가 복구 리로드를 시작하며 preventDefault하면 Vite 헬퍼는 import를 undefined로 끝낸다
        if (mod == null && isReloading()) return waitForReload();
        return mod;
      },
      (err: unknown) => {
        if (isChunkLoadError(err) && recoverFromChunkError()) return waitForReload(err); // 리로드 중 — 로더 유지(무산되면 10초 뒤 바운더리)
        throw err; // 복구 불가(60초 쿨다운) 또는 다른 오류 → 바운더리
      },
    ),
  );
}
